import pluralize from 'pluralize';
import { useMemo } from 'react';
import { TableWithHeader, TableWithHeaderProps } from './shared/TableWithHeader';
import { ObjectTypeLabelAndKeyTag } from '../../common/ObjectTypeLabelAndKey';
import Stack from '@mui/material/Stack';
import { TableTypography } from '../../common/table/utils';
import { TableItemHighlightedText } from '../dependencies/search/highlight/TableItemHighlightedText';
import { PossibleSectionInDependencies } from '../dependencies/search/types';
import { createStableLabelWrapper, markStyles } from '../dependencies/search/LabelWrapperComponent';
import { useSearchContext } from '../dependencies/search/SearchContext';

interface RelatedListTableProps extends Pick<TableWithHeaderProps, 'tableHeader'> {
  items: RelatedListTableItem[];
  section?: PossibleSectionInDependencies;
}

interface RelatedListTableItem {
  name: string;
  label: string;
  objectName: string;
}

const columns = [
  { field: 'label', headerName: 'Name', width: '180px' },
  { field: 'objectName', headerName: 'Object' },
];

const renderRows = ({
  items,
  section = 'details',
  ObjectNameLabelWrapper,
}: {
  items: RelatedListTableItem[];
  section?: PossibleSectionInDependencies;
  ObjectNameLabelWrapper: ReturnType<typeof createStableLabelWrapper>;
}) => {
  return items.map((row, index) => {
    return {
      id: row.name,
      label: (
        <TableTypography>
          <TableItemHighlightedText
            text={row.label}
            field="label"
            section={section}
            tableType="relatedList"
            rowIndex={index}
          />
        </TableTypography>
      ),
      objectName: (
        <>
          {row.objectName ? (
            <Stack minWidth={0} maxWidth="200px">
              <ObjectTypeLabelAndKeyTag
                objectType={row.objectName}
                LabelWrapperComponent={ObjectNameLabelWrapper}
                labelWrapperComponentSx={markStyles}
                labelWrapperComponentProps={{ rowIndex: index }}
              />
            </Stack>
          ) : (
            <TableTypography>N/A</TableTypography>
          )}
        </>
      ),
    };
  });
};

export const RelatedListTable = ({
  items,
  tableHeader,
  section = 'details',
  ...props
}: RelatedListTableProps) => {
  const { searchTerm } = useSearchContext();

  // Create stable wrapper component for highlighting objectName field
  const ObjectNameLabelWrapper = useMemo(
    () =>
      createStableLabelWrapper({
        field: 'objectName',
        section,
        tableType: 'relatedList',
      }),
    [section],
  );

  const rows = renderRows({
    items,
    section,
    ObjectNameLabelWrapper,
  });

  return (
    <TableWithHeader
      {...props}
      key={`tableHeader_${tableHeader}_${searchTerm}`}
      tableHeader={pluralize(tableHeader, rows.length)}
      columns={columns}
      rows={rows}
      dataTableType="relatedList"
      section={section}
      forceNonVirtualizedTable
    />
  );
};
